import React, { useEffect, useState } from 'react';
import styles from './AdminConfig.module.css';
import { ButtonCustom } from '../../BootstrapComponents/ButtonCustom';
import { ToastContainer, toast } from 'react-toastify';
import { BreathAPIs } from '../../APIs/BreathAPIs';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Modal } from 'antd';
const { confirm } = Modal;

const GUEST_CONFIG_ORDER = ['guestLoginEnabled', 'guestOtpRequired', 'guestMandatoryField'];

const renderConfigField = (config, disabled = false, cssStyles = {}) => {
    const checkboxLabelClass = disabled ? `form-check-label ${cssStyles['disabled-label']}` : 'form-check-label';
    const labelClass = disabled ? `form-label ${cssStyles['disabled-label']}` : 'form-label';
    if (config.type === 'checkbox') {
        return (
            <div key={config._id} className="mb-3">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id={config.configName} name={config.configName} defaultChecked={config.configValue} disabled={disabled} />
                    <label htmlFor={config.configName} className={checkboxLabelClass}>{config.configDisplayName}</label>
                </div>
            </div>
        );
    }
    if (config.type === 'select') {
        return (
            <div key={config._id} className="mb-3">
                <label htmlFor={config.configName} className={labelClass}>{config.configDisplayName}</label>
                <select
                    required
                    id={config.configName}
                    name={config.configName}
                    defaultValue={config.configValue}
                    className="form-select"
                    disabled={disabled}
                >
                    {(config.options || []).map(option => (
                        <option key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                        </option>
                    ))}
                </select>
            </div>
        );
    }
    return (
        <div key={config._id} className="mb-3">
            <label className={labelClass}>{config.configDisplayName}</label>
            <input
                required
                type={config.type}
                name={config.configName}
                defaultValue={config.configValue}
                className="form-control"
                disabled={disabled}
            />
        </div>
    );
};

const AdminConfig = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClearingLogs, setIsClearingLogs] = useState(false);
    const [configs, setConfigs] = useState([]);

    
    const [guestLoginOn, setGuestLoginOn] = useState(true);
    const [otpRequiredOn, setOtpRequiredOn] = useState(true);

    useEffect(() => {
        getConfig();
    }, []);

    useEffect(() => {
        const guestLoginConfig = configs.find(config => config.configName === 'guestLoginEnabled');
        if (guestLoginConfig) {
            setGuestLoginOn(!!guestLoginConfig.configValue);
        }
        const otpRequiredConfig = configs.find(config => config.configName === 'guestOtpRequired');
        if (otpRequiredConfig) {
            setOtpRequiredOn(!!otpRequiredConfig.configValue);
        }
    }, [configs]);

    const getConfig = async () => {
        setIsLoading(true);
        try {
            const res = await BreathAPIs.getConfigList();
            setConfigs(res.data.configs);
        } catch (error) {
            console.error(error);
            toast.error('Error fetching configurations.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const data = {};
            event.target.querySelectorAll('input, select').forEach(input => {
                if (input.type === 'checkbox') {
                    data[input.name] = input.checked;
                } else {
                    data[input.name] = input.value;
                }
            });
            await BreathAPIs.updateConfigs(data);
            toast.success('Configurations updated successfully.');
        } catch (error) {
            console.error(error);
            toast.error('Error updating configurations.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteLogs = async (event) => {
        try {
            setIsClearingLogs(true);
            await BreathAPIs.deleteLogs();
            toast.success('Logs Cleared');
        } catch (error) {
            console.error(error);
            toast.error('Error clearing logs.');
        } finally {
            setIsClearingLogs(false);
        }
    };

    const showDeleteConfirm = () => {
        confirm({
          title: 'Clear Logs',
          icon: <ExclamationCircleFilled />,
          content: 'Are you sure you want to proceed?',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk() {
            handleDeleteLogs();
          },
          onCancel() {},
        });
      };

    const guestConfigs = configs
        .filter(config => config.configName.startsWith('guest'))
        .sort((a, b) => GUEST_CONFIG_ORDER.indexOf(a.configName) - GUEST_CONFIG_ORDER.indexOf(b.configName));
    const otherConfigs = configs.filter(config => !config.configName.startsWith('guest'));

    return (
        <div>
            <div className='container mb-5 mb-md-6 mt-5'>
                <ToastContainer />
                <h2>Admin configuration</h2>
                <div className="justify-content-center">

                    <div className={styles['config-panel']}>
                        <p>Click this button to remove all existing logs and start fresh.</p>
                        <ButtonCustom onClick={showDeleteConfirm} isLoading={isClearingLogs} disabled={isClearingLogs || isLoading} text={'Clear logs'} />
                    </div>

                    <form onSubmit={handleSubmit}>
                        {(isLoading || guestConfigs.length > 0) && (
                            <div className={styles['config-panel']}>
                                <h5 className="mb-3">Guest Login</h5>
                                {isLoading ? <div>
                                    <p className="card-text placeholder-glow">
                                        <span className="placeholder col-7"></span>
                                        <span className="placeholder col-4"></span>
                                    </p>
                                </div> : null}
                                {guestConfigs.map(config => {
                                    if (config.configName === 'guestLoginEnabled') {
                                        return (
                                            <div key={config._id} className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={config.configName}
                                                        name={config.configName}
                                                        checked={guestLoginOn}
                                                        onChange={(e) => setGuestLoginOn(e.target.checked)}
                                                    />
                                                    <label htmlFor={config.configName} className="form-check-label">{config.configDisplayName}</label>
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (config.configName === 'guestOtpRequired') {
                                        return (
                                            <div key={config._id} className="mb-3">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={config.configName}
                                                        name={config.configName}
                                                        checked={otpRequiredOn}
                                                        disabled={!guestLoginOn}
                                                        onChange={(e) => setOtpRequiredOn(e.target.checked)}
                                                    />
                                                    <label htmlFor={config.configName} className={!guestLoginOn ? `form-check-label ${styles['disabled-label']}` : 'form-check-label'}>{config.configDisplayName}</label>
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (config.configName === 'guestMandatoryField') {
                                        const dynamicLabel = otpRequiredOn
                                            ? 'Send Guest Verification Code Via'
                                            : 'Guest Login Mandatory Field';
                                        return renderConfigField({ ...config, configDisplayName: dynamicLabel }, !guestLoginOn, styles);
                                    }
                                    return renderConfigField(config, !guestLoginOn, styles);
                                })}
                            </div>
                        )}

                        <div className={styles['config-panel']}>
                            {isLoading ? <div>
                                <p className="card-text placeholder-glow">
                                    <span className="placeholder col-7"></span>
                                    <span className="placeholder col-4"></span>
                                    <span className="placeholder col-4"></span>
                                    <span className="placeholder col-6"></span>
                                    <span className="placeholder col-8"></span>
                                </p>
                            </div> : null}
                            {otherConfigs.map(config => renderConfigField(config))}
                            <ButtonCustom isLoading={isSubmitting} disabled={isSubmitting || isLoading} type='submit' text={'Save'} />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AdminConfig;
